import React, { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";

const LoadingWithEstimate = ({
  message = "Redirecting to payment page...",
  duration = 2000,
  show = true,
  lang,
}) => {
  const [progress, setProgress] = useState(0);
  const frameRef = useRef(null);
  const startTimeRef = useRef(null);
  const durationRef = useRef(duration); // 👈 keep the latest duration always accessible

  // Update the durationRef silently (won’t trigger re-render)
  durationRef.current = duration;

  const changeLanguage = (lang) => {
    switch (lang) {
      case "mm":
        return "ငွေပေးချေမှု စာမျက်နှာသို့ ပြောင်းနေပါသည်...";
      case "ch":
        return "正在跳转到支付页面...";
      default:
        return "Redirecting to payment page...";
    }
  };

  const changeLanguageWait = (lang) => {
    switch (lang) {
      case "mm":
        return "ကျေးဇူးပြု၍ စောင့်ပါ၊ သင့်ငွေပေးချေမှုကို လုံခြုံစွာ ပြင်ဆင်နေပါသည်...";
      case "ch":
        return "请稍候，正在安全地准备您的付款...";
      default:
        return "Please wait, preparing your payment securely...";
    }
  };

  // 🔥 The animation runs only once — stable across prop changes
  useEffect(() => {
    let isCancelled = false;

    const step = (timestamp) => {
      if (isCancelled) return;
      if (!startTimeRef.current) startTimeRef.current = timestamp;

      const elapsed = timestamp - startTimeRef.current;
      const duration = durationRef.current; // read latest value
      const percent = Math.min((elapsed / duration) * 100, 100);
      setProgress(percent);

      if (percent < 100) {
        frameRef.current = requestAnimationFrame(step);
      }
    };

    frameRef.current = requestAnimationFrame(step);

    return () => {
      isCancelled = true;
      if (frameRef.current) cancelAnimationFrame(frameRef.current);
    };
  }, []); // 👈 runs once only!

  return (
    <div className="relative flex flex-col items-center justify-center h-[100dvh] bg-gradient-to-br from-[#e0f0ff] via-white to-[#f0eaff] text-gray-800 space-y-8 px-4 overflow-hidden">
      {/* Soft glowing orb */}
      <motion.div
        className="absolute w-64 h-64 sm:w-72 sm:h-72 md:w-80 md:h-80 bg-gradient-to-r from-indigo-400 via-blue-400 to-purple-400 blur-3xl opacity-30 rounded-full"
        animate={{ scale: [1, 1.1, 1] }}
        transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
      />

      {/* Main message */}
      <motion.div
        className="text-lg sm:text-xl font-semibold relative z-10 text-center leading-snug"
        animate={{ opacity: [0.7, 1, 0.7] }}
        transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
      >
        {changeLanguage(lang)}
      </motion.div>

      {/* Progress bar */}
      <div className="relative w-40 sm:w-48 md:w-48 lg:w-48 h-1 sm:h-1 bg-gray-200 rounded-full overflow-hidden shadow-inner z-10">
        <motion.div
          className="absolute h-full bg-gradient-to-r from-blue-500 via-indigo-500 to-purple-500 rounded-full shadow-md"
          style={{ width: `${progress}%` }}
          animate={{
            backgroundPosition: ["0% 50%", "100% 50%", "0% 50%"],
          }}
          transition={{
            duration: 2,
            ease: "linear",
            repeat: Infinity,
          }}
        />
      </div>

      {show && (
        <motion.div
          className="text-xs sm:text-sm text-gray-500 z-10 text-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6 }}
        >
          {changeLanguageWait(lang)}
        </motion.div>
      )}
    </div>
  );
};

export default LoadingWithEstimate;
